import React, { createContext, useContext, useState } from "react";
import "@/App.css";
import { Dialog, DialogContent } from "./components/ui/dialog";
import { ChevronDown, Download, MapPin, Home, Waves, Sun, Zap, ThermometerSun, Lightbulb, X, ChevronLeft, ChevronRight } from "lucide-react";

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("ES");
  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations
const translations = {
  RU: {
    subtitle: "Исторический центр Эстепоны",
    tagline: "Недвижимость на продажу с утверждённым проектом и разрешением на строительство",
    approved: "Лицензия одобрена",
    readyToBuild: "Готово к строительству",
    whatIsSold: "Что продаётся?",
    whatIsSoldDesc: "Продаётся недвижимость в её текущем состоянии вместе с полным архитектурным проектом и разрешением на строительство, уже утверждённым мэрией. Вы можете начать строительство немедленно.",
    saleIncludes: "В продажу входит",
    includes: [
      "Недвижимость в текущем состоянии (под ремонт)",
      "Полный архитектурный проект",
      "Утверждённое разрешение на строительство",
      "Подробные строительные чертежи"
    ],
    bedrooms: "Спальни",
    construction: "Строительство",
    floors: "Этажи",
    license: "Лицензия",
    twoOptions: "Два варианта развития",
    luxuryHome: "Роскошный дом",
    luxuryHomeDesc: "4 спальни на 3 этажах плюс башня, с частным бассейном на крыше и солярием с великолепным видом.",
    expansion: "Возможность расширения",
    expansionDesc: "Проект позволяет расширение до 260м² для превращения недвижимости в эксклюзивный бутик-отель в самом сердце Старого города.",
    projectDesign: "Дизайн проекта",
    projectDesignDesc: "Визуализация утверждённого проекта реконструкции",
    distribution: "Планировка",
    totalSurface: "Общая построенная площадь",
    groundFloor: "Первый этаж",
    firstFloor: "Второй этаж",
    tower: "Башня",
    rooftop: "Крыша",
    groundFloorRooms: [
      { name: "Прихожая", size: "20м²" },
      { name: "Гостиная-Столовая-Кухня", size: "37м²" },
      { name: "Гостевая зона", size: "11м²" },
      { name: "Гостевая спальня", size: "6м²" },
      { name: "Гостевая ванная", size: "3м²" },
      { name: "Ванная", size: "6м²" }
    ],
    firstFloorRooms: [
      { name: "Главная спальня", size: "18м²" },
      { name: "Главная ванная", size: "3м²" },
      { name: "Спальня 2", size: "13м²" },
      { name: "Ванная", size: "4м²" },
      { name: "Коридор", size: "10м²" }
    ],
    towerRooms: [
      { name: "Спальня", size: "13м²" },
      { name: "Зона отдыха", size: "" },
      { name: "Выход на террасу", size: "" }
    ],
    rooftopRooms: [
      { name: "Частный бассейн", size: "5м²" },
      { name: "Терраса-солярий", size: "" },
      { name: "Солнечные панели", size: "" }
    ],
    features: "Особенности",
    rooftopPool: "Бассейн на крыше",
    solarPanels: "Солнечные панели",
    aerothermal: "Аэротермия",
    underfloorHeating: "Тёплый пол",
    integratedLed: "Встроенное LED-освещение",
    viewPlans: "Смотреть полные планы",
    plansDesc: "PDF-документ с подробными архитектурными планами",
    currentState: "Текущее состояние недвижимости",
    currentStateDesc: "Это то, что продаётся: недвижимость под реновацию",
    location: "Расположение",
    locationName: "Исторический центр Эстепоны",
    estepona: "Эстепона",
    esteponaTagline: "Сад Коста-дель-Соль",
    esteponaDesc: "Эстепона — один из самых очаровательных городов Коста-дель-Соль. С её старинным центром с белыми улочками, украшенными цветами, золотистыми песчаными пляжами и подлинной андалузской атмосферой — это идеальное место для средиземноморского образа жизни.",
    contactAgent: "Свяжитесь с вашим агентом по недвижимости",
    interestedText: "Заинтересованы в этой уникальной возможности?",
    oldTown: "Исторический центр Эстепоны",
    flowerStreets: "Цветочные улицы",
    beach: "Пляж Эстепоны"
  },
  ES: {
    subtitle: "Casco Antiguo de Estepona",
    tagline: "Propiedad en venta con proyecto y licencia de obra aprobada",
    approved: "Licencia Aprobada",
    readyToBuild: "Lista para construir",
    whatIsSold: "¿Qué se vende?",
    whatIsSoldDesc: "Se vende la propiedad en su estado actual junto con el proyecto arquitectónico completo y la licencia de obra ya aprobada por el Ayuntamiento. Puedes empezar la construcción de inmediato.",
    saleIncludes: "La venta incluye",
    includes: [
      "Propiedad en estado actual (a reformar)",
      "Proyecto arquitectónico completo",
      "Licencia de obra aprobada",
      "Planos de ejecución detallados"
    ],
    bedrooms: "Dormitorios",
    construction: "Construcción",
    floors: "Plantas",
    license: "Licencia",
    twoOptions: "Dos opciones de desarrollo",
    luxuryHome: "Vivienda de Lujo",
    luxuryHomeDesc: "4 dormitorios distribuidos en 3 plantas más torreón, con piscina privada en azotea y terraza solarium con vistas espectaculares.",
    expansion: "Posibilidad de Ampliación",
    expansionDesc: "El proyecto permite ampliar hasta 260m² para convertir la propiedad en un exclusivo hotel boutique en el corazón del Casco Antiguo.",
    projectDesign: "El Diseño del Proyecto",
    projectDesignDesc: "Visualización del proyecto de reforma aprobado",
    distribution: "Distribución",
    totalSurface: "Superficie total construida",
    groundFloor: "Planta Baja",
    firstFloor: "Primera Planta",
    tower: "Torreón",
    rooftop: "Azotea",
    groundFloorRooms: [
      { name: "Recibidor", size: "20m²" },
      { name: "Estar-Comedor-Cocina", size: "37m²" },
      { name: "Estar Invitados", size: "11m²" },
      { name: "Dormitorio Invitados", size: "6m²" },
      { name: "Baño Invitados", size: "3m²" },
      { name: "Baño", size: "6m²" }
    ],
    firstFloorRooms: [
      { name: "Dormitorio Principal", size: "18m²" },
      { name: "Baño Principal", size: "3m²" },
      { name: "Dormitorio 2", size: "13m²" },
      { name: "Baño", size: "4m²" },
      { name: "Distribuidor", size: "10m²" }
    ],
    towerRooms: [
      { name: "Dormitorio", size: "13m²" },
      { name: "Zona de Estar", size: "" },
      { name: "Acceso a Terraza", size: "" }
    ],
    rooftopRooms: [
      { name: "Piscina Privada", size: "5m²" },
      { name: "Terraza Solarium", size: "" },
      { name: "Placas Solares", size: "" }
    ],
    features: "Características Destacadas",
    rooftopPool: "Piscina en azotea",
    solarPanels: "Placas solares",
    aerothermal: "Aerotermia",
    underfloorHeating: "Suelo radiante",
    integratedLed: "LED integrado",
    viewPlans: "Ver Planos Completos",
    plansDesc: "Documento PDF con planos arquitectónicos detallados",
    currentState: "Estado Actual de la Propiedad",
    currentStateDesc: "Esto es lo que se vende: la propiedad a rehabilitar",
    location: "Ubicación",
    locationName: "Casco Antiguo de Estepona",
    estepona: "Estepona",
    esteponaTagline: "El Jardín de la Costa del Sol",
    esteponaDesc: "Estepona es uno de los pueblos más encantadores de la Costa del Sol. Con su casco antiguo de calles blancas adornadas con flores, sus playas de arena dorada y su ambiente auténtico andaluz, es el lugar perfecto para vivir el estilo de vida mediterráneo.",
    contactAgent: "Contacte con su agente inmobiliario",
    interestedText: "¿Interesado en esta oportunidad única?",
    oldTown: "Casco Antiguo de Estepona",
    flowerStreets: "Calles con flores",
    beach: "Playa de Estepona"
  },
  EN: {
    subtitle: "Estepona Old Town",
    tagline: "Property for sale with approved project and building license",
    approved: "License Approved",
    readyToBuild: "Ready to build",
    whatIsSold: "What is included?",
    whatIsSoldDesc: "The property is sold in its current state together with the complete architectural project and the building license already approved by the Town Hall. You can start construction immediately.",
    saleIncludes: "The sale includes",
    includes: [
      "Property in current state (to be renovated)",
      "Complete architectural project",
      "Approved building license",
      "Detailed execution plans"
    ],
    bedrooms: "Bedrooms",
    construction: "Construction",
    floors: "Floors",
    license: "License",
    twoOptions: "Two development options",
    luxuryHome: "Luxury Home",
    luxuryHomeDesc: "4 bedrooms distributed over 3 floors plus tower, with private rooftop pool and solarium terrace with spectacular views.",
    expansion: "Expansion Possibility",
    expansionDesc: "The project allows expansion up to 260m² to convert the property into an exclusive boutique hotel in the heart of the Old Town.",
    projectDesign: "The Project Design",
    projectDesignDesc: "Visualization of the approved renovation project",
    distribution: "Distribution",
    totalSurface: "Total built surface",
    groundFloor: "Ground Floor",
    firstFloor: "First Floor",
    tower: "Tower",
    rooftop: "Rooftop",
    groundFloorRooms: [
      { name: "Entrance Hall", size: "20m²" },
      { name: "Living-Dining-Kitchen", size: "37m²" },
      { name: "Guest Living Area", size: "11m²" },
      { name: "Guest Bedroom", size: "6m²" },
      { name: "Guest Bathroom", size: "3m²" },
      { name: "Bathroom", size: "6m²" }
    ],
    firstFloorRooms: [
      { name: "Master Bedroom", size: "18m²" },
      { name: "Master Bathroom", size: "3m²" },
      { name: "Bedroom 2", size: "13m²" },
      { name: "Bathroom", size: "4m²" },
      { name: "Hallway", size: "10m²" }
    ],
    towerRooms: [
      { name: "Bedroom", size: "13m²" },
      { name: "Living Area", size: "" },
      { name: "Terrace Access", size: "" }
    ],
    rooftopRooms: [
      { name: "Private Pool", size: "5m²" },
      { name: "Solarium Terrace", size: "" },
      { name: "Solar Panels", size: "" }
    ],
    features: "Highlighted Features",
    rooftopPool: "Rooftop pool",
    solarPanels: "Solar panels",
    aerothermal: "Aerothermal",
    underfloorHeating: "Underfloor heating",
    integratedLed: "Integrated LED",
    viewPlans: "View Complete Plans",
    plansDesc: "PDF document with detailed architectural plans",
    currentState: "Current State of the Property",
    currentStateDesc: "This is what is sold: the property to be renovated",
    location: "Location",
    locationName: "Estepona Old Town",
    estepona: "Estepona",
    esteponaTagline: "The Garden of the Costa del Sol",
    esteponaDesc: "Estepona is one of the most charming towns on the Costa del Sol. With its old town of white streets adorned with flowers, its golden sandy beaches and its authentic Andalusian atmosphere, it is the perfect place to live the Mediterranean lifestyle.",
    contactAgent: "Contact your real estate agent",
    interestedText: "Interested in this unique opportunity?",
    oldTown: "Estepona Old Town",
    flowerStreets: "Flower streets",
    beach: "Estepona Beach"
  }
};

// Assets
const assets = {
  hero: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/ucmohgla_imagen%20web-02.png",
  renders: [
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/ucmohgla_imagen%20web-02.png", alt: "Fachada del proyecto" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/zn98cfan_1.jpg", alt: "Patio exterior con torreón" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/o6crsko2_3.jpg", alt: "Escaleras interiores" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/17oc6ntc_4.jpg", alt: "Dormitorio principal" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/7ec5torj_5.jpg", alt: "Vestidor" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/afcqiygn_6.jpg", alt: "Dormitorio con baño" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/4lzwe1qd_7.jpg", alt: "Baño principal" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/e1td42h0_8.jpg", alt: "Baño doble lavabo" },
    { url: "https://customer-assets.emergentagent.com/job_estepona-listing/artifacts/002krckh_9.jpg", alt: "Suite con cristalera" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/46dhrs4c_WhatsApp%20Image%202026-01-26%20at%2012.29.47.jpeg", alt: "Salón" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/x4hx81s5_WhatsApp%20Image%202026-01-26%20at%2012.29.58.jpeg", alt: "Fachada exterior" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/22iln1vb_WhatsApp%20Image%202026-01-26%20at%2012.30.16.jpeg", alt: "Cocina" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/orm0vjw7_WhatsApp%20Image%202026-01-26%20at%2012.30.23.jpeg", alt: "Escaleras" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/vqof7qi8_WhatsApp%20Image%202026-01-26%20at%2012.30.30.jpeg", alt: "Entrada" }
  ],
  currentState: [
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/bobd9wkm_WhatsApp%20Image%202026-01-28%20at%2013.59.20.jpeg", alt: "Current Facade" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/72wevyxq_WhatsApp%20Image%202026-01-28%20at%2015.34.04%20%281%29.jpeg", alt: "Current Patio" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/gl54cyi1_WhatsApp%20Image%202026-01-28%20at%2015.34.04.jpeg", alt: "Current Interior" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/gdsyrzow_WhatsApp%20Image%202026-01-28%20at%2013.59.20%20%281%29.jpeg", alt: "Interior" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/moqmllzs_WhatsApp%20Image%202026-01-28%20at%2013.59.20%20%282%29.jpeg", alt: "Room" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/yl554un7_WhatsApp%20Image%202026-01-28%20at%2013.59.20%20%283%29.jpeg", alt: "Exterior View" }
  ],
  estepona: [
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/x4jyw7yd_Estepona%201.png", alt: "Casco Antiguo de Estepona" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/0wttnjh4_estepona%202.jpg", alt: "Calles con flores" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/i0qn6go5_estepona%203.avif", alt: "Playa de Estepona" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/17evpe1f_estepona%204.jpg", alt: "Atardecer en la playa" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/6z6eymqu_Estepona%205.jpg", alt: "Vista panorámica" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/2adg5329_Estepona%207.webp", alt: "Puesta de sol" },
    { url: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/dsegwmdx_Estepona%208.jpg", alt: "Costa del Sol" }
  ],
  plansPdf: "https://customer-assets.emergentagent.com/job_803ea0a9-3d70-4dd0-b0b2-088e151e6735/artifacts/c0h0rs6m_EXECUTIVE%20DRAWINGS%202026.pdf"
};

// Language Toggle Component
const LanguageToggle = () => {
  const { lang, setLang } = useLanguage();
  const languages = ["ES", "EN", "RU"];
  return (
    <div className="flex items-center gap-3 text-sm font-medium" data-testid="language-toggle">
      {languages.map((l, idx) => (
        <React.Fragment key={l}>
          <button
            onClick={() => setLang(l)}
            className={`transition-all duration-300 ${lang === l ? "text-[#2C3328] font-semibold" : "text-[#5A6158] hover:text-[#2C3328]"}`}
            data-testid={`lang-${l.toLowerCase()}-btn`}
          >
            {l}
          </button>
          {idx < languages.length - 1 && <span className="text-[#E5E0D8]">|</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  if (currentIndex === null) return null;
  
  return (
    <Dialog open={currentIndex !== null} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 text-white/80 hover:text-white transition-colors"
          data-testid="lightbox-close"
        >
          <X className="w-6 h-6" />
        </button>
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-2 text-white/80 hover:text-white transition-colors"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-2 text-white/80 hover:text-white transition-colors"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
        <div className="flex items-center justify-center w-full h-[90vh]">
          <img
            src={images[currentIndex]?.url}
            alt={images[currentIndex]?.alt}
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hero Section
const HeroSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <section className="relative min-h-screen flex items-end" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={assets.hero}
          alt="Papuecas"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
      </div>

      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="glass-effect px-4 py-2 rounded-sm">
            <LanguageToggle />
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full px-6 md:px-12 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-fade-in-up">
            <span className="inline-block text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              {t.approved} · {t.readyToBuild}
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-medium text-white mb-4 animate-fade-in-up animation-delay-100">
            Papuecas
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-light mb-2 animate-fade-in-up animation-delay-200">
            {t.subtitle}
          </p>
          <p className="text-lg text-white/70 font-light max-w-xl mx-auto animate-fade-in-up animation-delay-300">
            {t.tagline}
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <ChevronDown className="w-6 h-6 text-white/60" />
      </div>
    </section>
  );
};

// What Is Sold Section
const WhatIsSoldSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-[#F9F8F6]" data-testid="what-is-sold-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Left Column */}
          <div>
            <span className="text-[#C77D63] text-sm tracking-[0.2em] uppercase mb-4 block">
              {t.saleIncludes}
            </span>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C3328] mb-6">
              {t.whatIsSold}
            </h2>
            <p className="text-lg text-[#5A6158] leading-relaxed mb-8">
              {t.whatIsSoldDesc}
            </p>
            <ul className="space-y-4">
              {t.includes.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C77D63] mt-2.5 flex-shrink-0" />
                  <span className="text-[#2C3328]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column - Stats */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-8 border border-[#E5E0D8]">
              <span className="text-5xl font-medium text-[#2C3328] block mb-2">4</span>
              <span className="text-[#5A6158] text-sm tracking-wide">{t.bedrooms}</span>
            </div>
            <div className="bg-white p-8 border border-[#E5E0D8]">
              <span className="text-5xl font-medium text-[#2C3328] block mb-2">~180</span>
              <span className="text-[#5A6158] text-sm tracking-wide">m² {t.construction}</span>
            </div>
            <div className="bg-white p-8 border border-[#E5E0D8]">
              <span className="text-5xl font-medium text-[#2C3328] block mb-2">3+1</span>
              <span className="text-[#5A6158] text-sm tracking-wide">{t.floors}</span>
            </div>
            <div className="bg-white p-8 border border-[#E5E0D8]">
              <span className="text-3xl font-medium text-[#D4AF37] block mb-2">✓</span>
              <span className="text-[#5A6158] text-sm tracking-wide">{t.license}</span>
            </div>
          </div>
        </div>

        {/* Two Options */}
        <div className="mt-20 grid md:grid-cols-2 gap-8">
          <div className="bg-white p-10 border border-[#E5E0D8]">
            <Home className="w-8 h-8 text-[#C77D63] mb-4" />
            <h3 className="text-2xl font-medium text-[#2C3328] mb-4">{t.luxuryHome}</h3>
            <p className="text-[#5A6158] leading-relaxed">{t.luxuryHomeDesc}</p>
          </div>
          <div className="bg-white p-10 border border-[#E5E0D8]">
            <Sun className="w-8 h-8 text-[#C77D63] mb-4" />
            <h3 className="text-2xl font-medium text-[#2C3328] mb-4">{t.expansion}</h3>
            <p className="text-[#5A6158] leading-relaxed">{t.expansionDesc}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Renders Gallery Section
const RendersGallery = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const handlePrev = () => {
    setLightboxIndex((prev) => (prev === 0 ? assets.renders.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setLightboxIndex((prev) => (prev === assets.renders.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-[#2C3328]" data-testid="renders-gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
            {t.projectDesign}
          </span>
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-4">
            {t.projectDesignDesc}
          </h2>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {assets.renders.map((image, idx) => (
            <div
              key={idx}
              className={`relative overflow-hidden cursor-pointer group ${idx === 0 ? "col-span-2 row-span-2" : ""}`}
              onClick={() => setLightboxIndex(idx)}
              data-testid={`render-image-${idx}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover image-hover aspect-square"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        images={assets.renders}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
};

// Distribution Section
const DistributionSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  const floorData = [
    { key: "ground", label: t.groundFloor, size: "84 m²", rooms: t.groundFloorRooms },
    { key: "first", label: t.firstFloor, size: "48 m²", rooms: t.firstFloorRooms },
    { key: "tower", label: t.tower, size: "26 m²", rooms: t.towerRooms },
    { key: "rooftop", label: t.rooftop, size: "32 m²", rooms: t.rooftopRooms }
  ];

  const features = [
    { icon: Waves, label: t.rooftopPool },
    { icon: Sun, label: t.solarPanels },
    { icon: ThermometerSun, label: t.aerothermal },
    { icon: Zap, label: t.underfloorHeating },
    { icon: Lightbulb, label: t.integratedLed }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-[#F9F8F6]" data-testid="distribution-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C3328] mb-4">
            {t.distribution}
          </h2>
          <p className="text-[#5A6158] text-lg">
            {t.totalSurface}: <span className="font-semibold text-[#2C3328] text-xl">~180 m²</span>
          </p>
        </div>

        {/* Floor Plans Grid - 4 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {floorData.map((floor) => (
            <div key={floor.key} className="bg-white border border-[#E5E0D8] p-6" data-testid={`floor-${floor.key}`}>
              <div className="border-b border-[#E5E0D8] pb-4 mb-4">
                <h3 className="text-xl font-medium text-[#2C3328] mb-1">{floor.label}</h3>
                <span className="text-2xl font-semibold text-[#C77D63]">{floor.size}</span>
              </div>
              <ul className="space-y-2">
                {floor.rooms.map((room, idx) => (
                  <li key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-[#5A6158]">{room.name}</span>
                    {room.size && <span className="text-[#2C3328] font-medium">{room.size}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="bg-white border border-[#E5E0D8] p-8 mb-12">
          <h3 className="text-xl font-medium text-[#2C3328] text-center mb-6">{t.features}</h3>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[#5A6158]">
                <feature.icon className="w-5 h-5 text-[#C77D63]" />
                <span>{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Download Plans */}
        <div className="text-center">
          <a
            href={assets.plansPdf}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#2C3328] text-white hover:bg-[#3d4539] transition-all duration-300"
            data-testid="download-plans-btn"
          >
            <Download className="w-5 h-5" />
            {t.viewPlans}
          </a>
          <p className="text-[#5A6158] text-sm mt-4">{t.plansDesc}</p>
        </div>
      </div>
    </section>
  );
};

// Current State Section
const CurrentStateSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const handlePrev = () => {
    setLightboxIndex((prev) => (prev === 0 ? assets.currentState.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setLightboxIndex((prev) => (prev === assets.currentState.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="current-state-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-[#C77D63] text-sm tracking-[0.2em] uppercase mb-4 block">
            {t.currentState}
          </span>
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C3328] mb-4">
            {t.currentStateDesc}
          </h2>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {assets.currentState.map((image, idx) => (
            <div
              key={idx}
              className="relative overflow-hidden cursor-pointer group aspect-square"
              onClick={() => setLightboxIndex(idx)}
              data-testid={`current-state-image-${idx}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover image-hover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        images={assets.currentState}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
};

// Location Section
const LocationSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-[#F9F8F6]" data-testid="location-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C3328] mb-4">
            {t.location}
          </h2>
          <div className="flex items-center justify-center gap-2 text-[#5A6158]">
            <MapPin className="w-5 h-5 text-[#C77D63]" />
            <span className="text-lg">{t.locationName}</span>
          </div>
        </div>

        {/* Map Placeholder */}
        <div className="relative w-full h-[400px] bg-[#E5E0D8] overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3218.0621839673595!2d-5.1479!3d36.4275!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd0ce52c7c7e1e1f%3A0x7!2sCasco%20Antiguo%20de%20Estepona!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación Estepona"
          />
        </div>
      </div>
    </section>
  );
};

// Estepona Section
const EsteponaSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const handlePrev = () => {
    setLightboxIndex((prev) => (prev === 0 ? assets.estepona.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setLightboxIndex((prev) => (prev === assets.estepona.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="estepona-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center mb-16">
          <div>
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              {t.esteponaTagline}
            </span>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C3328] mb-6">
              {t.estepona}
            </h2>
            <p className="text-lg text-[#5A6158] leading-relaxed">
              {t.esteponaDesc}
            </p>
          </div>
          <div className="overflow-hidden cursor-pointer" onClick={() => setLightboxIndex(0)}>
            <img
              src={assets.estepona[0].url}
              alt={t.oldTown}
              className="w-full h-auto object-cover image-hover"
            />
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {assets.estepona.slice(1).map((image, idx) => (
            <div
              key={idx}
              className="relative overflow-hidden cursor-pointer group aspect-[4/3]"
              onClick={() => setLightboxIndex(idx + 1)}
              data-testid={`estepona-image-${idx}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover image-hover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        images={assets.estepona}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
};

// Footer Section
const FooterSection = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <footer
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{
        backgroundImage: `url(${assets.renders[0].url})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
      data-testid="footer-section"
    >
      <div className="absolute inset-0 bg-[#2C3328]/90" />
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
          {t.interestedText}
        </h2>
        <div className="inline-block px-8 py-4 border-2 border-[#D4AF37] text-[#D4AF37] text-xl font-medium">
          {t.contactAgent}
        </div>
        <p className="mt-12 text-white/60 text-sm">
          Papuecas · {t.locationName}
        </p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <LanguageProvider>
      <div className="App" data-testid="papuecas-app">
        <HeroSection />
        <WhatIsSoldSection />
        <RendersGallery />
        <DistributionSection />
        <CurrentStateSection />
        <LocationSection />
        <EsteponaSection />
        <FooterSection />
      </div>
    </LanguageProvider>
  );
}

export default App;
